import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Dropdown } from "simplydo/react/ui/dropdown";

import type { ModalSize } from "./Modal.types";

export const DimmerOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.85);
  overflow: auto;
  box-sizing: border-box;
`;

function sizeMaxWidth(size?: ModalSize, fullscreen?: boolean): string {
  // Always stay within the dimmer's content box (viewport minus padding).
  if (fullscreen) {
    return "95%";
  }
  switch (size) {
    case "small":
      return "min(720px, 100%)";
    case "large":
      return "min(1080px, 100%)";
    default:
      return "min(900px, 100%)";
  }
}

export const HeaderEl = styled.div<{ $basic?: boolean }>`
  font-size: 1.42857143rem;
  font-weight: bold;
  line-height: 1.2857em;
  padding: 1.25rem 1.5rem;
  border-bottom: ${({ $basic }) =>
    $basic ? "none" : "1px solid rgba(34, 36, 38, 0.15)"};
  color: inherit;
  flex-shrink: 0;
  min-width: 0;
  box-sizing: border-box;
`;

export const ContentEl = styled.div`
  position: relative;
  padding: 1.5rem;
  line-height: 1.4;
  color: inherit;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  overflow-x: auto;

  &.is-scrolling {
    overflow: auto;
    max-height: 80vh;
  }
`;

export const DescriptionEl = styled.div`
  color: inherit;
  line-height: 1.4;
  min-width: 0;

  & + & {
    margin-top: 1rem;
  }
`;

export const ActionsEl = styled.div<{ $basic?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 0.45rem;
  padding: 1rem 1.5rem;
  border-top: ${({ $basic }) =>
    $basic ? "none" : "1px solid rgba(34, 36, 38, 0.15)"};
  background: ${({ $basic }) => ($basic ? "transparent" : "#f9fafb")};
  flex-shrink: 0;
  min-width: 0;
  box-sizing: border-box;

  border-radius: 0 0 4px 4px;

  /* Space sibling Buttons when callers omit gap-friendly layout */
  > ${Button}, > ${Dropdown} ${Button} {
    margin: 0 !important;
  }
`;

export const Dialog = styled.div<{ $size?: ModalSize; $fullscreen?: boolean }>`
  position: relative;
  z-index: 1001;
  outline: none;
  border-radius: 4px;
  box-sizing: border-box;
  width: 100%;
  max-width: ${({ $size, $fullscreen }) => sizeMaxWidth($size, $fullscreen)};
  /* Override flex default min-width:auto so wide children cannot expand
     the dialog past the viewport. */
  min-width: 0;
  margin: 0 auto;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  box-shadow:
    1px 3px 3px 0 rgba(34, 36, 38, 0.15),
    1px 3px 15px 2px rgba(34, 36, 38, 0.15);
  background: #ffffff;
  color: rgba(0, 0, 0, 0.87);
  border: none;

  &.is-fullscreen {
    margin: 0;
  }

  &.is-basic {
    background: transparent;
    color: #ffffff;

    --border-color: rgb(118 118 118 / 20%);

    background-color: rgb(0 0 0 / 20%) !important;
    border-radius: 6px !important;
    border: 1px solid var(--border-color) !important;
    box-shadow: 2px 4px 10px 0 rgba(0, 0, 0, 0.2) !important;

    ${HeaderEl} {
      border-bottom: 1px solid var(--border-color) !important;
    }

    ${ActionsEl} {
      border-top: 1px solid var(--border-color) !important;
    }
  }
`;
